<template>
  <div
    class="text control"
    :data-field="externalKey"
  >
    <label
      v-if="showLabel"
      class="text__label"
    >
      {{ label }}{{ measureUnit }}
    </label>
    <div 
      :class="{
        'text__display': true,
        'text__display--disabled': isDisabled
      }"
      @click="setInputStatus(true)"
    >
      {{ displayValue }}
    </div>
    <input
      v-show="isInputActive"
      ref="input"
      :disabled="isDisabled"
      :value="value"
      class="text__input control-box"
      type="text"
      @blur="setInputStatus(false)"
      @keypress="keypressHandler"
      @keyup="keyupHandler"
      @change="changeHandler"
    >
  </div>
</template>

<script>
import wNumb from 'wnumb';

export default {
  name: 'ControlText',
  props: {
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    measureUnitTitle: {
      type: String,
      default: 'Р'
    },
    externalKey: {
      type: String,
      default: ''
    },
    externalHandler: {
      type: Function,
      default: () => {}
    },
    externalChangeHandler: {
      type: Function,
      default: () => {}
    },
    defaultValue: {
      type: [Number, String],
      default: ''
    },
    defaultDisplayValue: {
      type: [Number, String],
      default: ''
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      value: this.defaultValue,
      isInputActive: false,
      moneyFormat: wNumb({
        thousand: ' '
      })
    };
  },
  computed: {
    valueToNumber() {
      return parseInt(this.value);
    },
    valueWithDivisions() {
      const formatted = this.moneyFormat.to(this.valueToNumber);
      
      return formatted ? formatted : '';
    },
    displayValue() {
      return this.defaultDisplayValue || this.valueWithDivisions;
    },
    measureUnit() {
      return this.measureUnitTitle ? ', ' + this.measureUnitTitle : '';
    },
    showLabel() {
      return this.label ? true : false;
    }
  },
  watch: {
    isInputActive(newVal) {
      if (newVal) {
        this.$refs.input.focus();
      }
    },
    defaultValue(newVal) {
      if (newVal !== this.value) {
        this.value = newVal;
      }
    }
  },
  methods: {
    keypressHandler(evt) {
      if (evt.which < 48 || evt.which > 57 || parseInt(evt.target.value) > 1e+14) {
        evt.preventDefault();
      }
    },
    keyupHandler(evt) {
      const { target: { value } } = evt;
      const isZeroSequence = /^0+/gi.test(value);

      // in case of non-numeric input
      if (value === '' || this.value === value || (isZeroSequence && value !== '0')) {
        return false;
      }

      this.value = value;
      this.externalHandler({ key: this.externalKey, val: this.valueToNumber });
    },
    changeHandler(evt) {
      const { target: { value } } = evt;

      if (value === '') {
        this.value = this.defaultValue;
        this.externalHandler({ key: this.externalKey, val: this.valueToNumber });
      }
      
      this.externalChangeHandler(this.value);
    },
    setInputStatus(status) {
      this.isInputActive = status;

      if (status) {
        // elements with display: none/visibility: hidden doesn't allow to focus so we trigger it with delay
        // https://stackoverflow.com/questions/21351476/why-changing-visibility-display-on-focus-does-not-work
        this.$nextTick(this.forceInputFocus);
      }
    },
    forceInputFocus() {
      this.$refs.input.focus();
    }
  }
};
</script>

<style lang="scss" scoped>
.text {
  width: 100%;
  min-width: calcEm(100px);
  position: relative;

  &__display,
  &__input {
    width: 100%;
    padding: 0 calcEm(12px);
    border: 1px solid $color-grey;
    @include transitionBase(all);

    &:hover,
    &:focus {
      border-color: $color-black;
    }
  }

  &__display {
    height: calcEm(48px);
    height: 48px;
    box-sizing: border-box;
    background: $color-white;
    display: flex;
    align-items: center;
  }

  &__input {
    position: absolute;
    left: 0;
    bottom: 0;
    z-index: 10;
    outline: none;
  }

  &__input:disabled,
  &__display--disabled {
    background-color: #f7f7f7;
  }
    

  &__label {
    font-size: calcEm(16px);
    font-family: $font-family-medium;
  }
}

@include mobile {
  .text {
    &__display {
      height: calcEm(56px);
    }
  }
}
</style>
